// H2 数据分配（TDesign）：导入excel + 搜索 + 列表勾选 + 批量编辑弹窗(兼职树/话术/二维码/备注) + 发送 + 分页
import React, { useState, useEffect, useCallback } from 'react';
import {
  Button, Space, Input, Select, Textarea, Upload, Table, Descriptions, MessagePlugin,
  Tag, Tooltip, Dialog, Loading,
} from 'tdesign-react';
import { parttimeApi } from '../../api/parttime';
import { getPageSize } from '../../api/parttimePager';
import WorkerTree from './WorkerTree';

// 全局配置 key（分页每页条数），后端读取，前端不写死
const PAGE_SIZE_KEY = 'page_size_H2';

// 处理状态下拉（本页仅待处理池：未分配 / 已分配未发送 / 分配后撤回 / 分配后拒绝；已发送及之后状态的数据在「数据核对」页）
const STATUS_OPTS = [
  { label: '全部', value: 'ALL' },
  { label: '未分配', value: 'UNASSIGNED' },
  { label: '已分配未发送', value: 'PRE_ASSIGNED' },
  { label: '分配后撤回', value: 'RECALLED' },
  { label: '分配后拒绝', value: 'REJECTED' },
];
// 表格展示用全量标签（UNSUBMITTED 为旧数据兼容值，映射为「未分配」）
const STATUS_LABEL = {
  UNASSIGNED: '未分配', UNSUBMITTED: '未分配', PRE_ASSIGNED: '已分配未发送', SENT: '已发送未领取', CLAIMED: '已领取进行中',
  SUBMITTED: '已截图待审核', PASSED: '截图审核通过', FAILED: '截图审核不通过',
  RECALLED: '分配后撤回', REJECTED: '分配后拒绝',
};
// 处理状态标签主题色（与数据核对/对账结算页一致）
const statusTheme2 = (s) => {
  if (s === 'PASSED') return 'success';
  if (s === 'FAILED' || s === 'REJECTED') return 'danger';
  if (s === 'CLAIMED') return 'primary';
  if (s === 'SUBMITTED' || s === 'SENT' || s === 'PRE_ASSIGNED' || s === 'RECALLED') return 'warning';
  return 'default';
};
// 对账状态标签与主题色
const SETTLE_LABEL2 = { UNREVIEWED: '未对账', SETTLEMENTING: '对账中', CONFIRMED: '对账通过', DISAGREED: '对账未通过' };
const settleTheme2 = (s) => {
  if (s === 'CONFIRMED') return 'success';
  if (s === 'DISAGREED') return 'danger';
  if (s === 'SETTLEMENTING') return 'warning';
  return 'default';
};

/**
 * H2 数据分配页面
 */
export default function ParttimeImportAllocate() {
  // ---- 搜索条件（live，点击查询才应用） ----
  const [filters, setFilters] = useState({ ownerName: '', phone: '', wechat: '', community: '', city: '', status: 'ALL', workerName: '' });
  const [q, setQ] = useState(filters);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(500);
  const [sel, setSel] = useState(() => new Set());   // 勾选的行 dataId
  // 查询强制刷新标记：保证无搜索条件时点击「查询」也能触发接口（避免 setState 相同引用被 React 跳过）
  const [reloadKey, setReloadKey] = useState(0);
  // 请求中标记：保存/发送/删除 按钮 loading，防止重复提交
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [tree, setTree] = useState([]);

  // 编辑器弹窗：mode = worker|script|qrcode|remark|full（cell 点击展示全部）
  const [editor, setEditor] = useState(null); // { mode, dataIds }
  const [planState, setPlanState] = useState({});
  const [scriptVal, setScriptVal] = useState('');
  const [qrcodeList, setQrcodeList] = useState([]); // 已上传的群二维码 [{fileId,url}]
  const [remarkVal, setRemarkVal] = useState('');

  const [viewRow, setViewRow] = useState(null); // 查看详情
  const [viewImg, setViewImg] = useState(null); // 全屏预览图片
  const [delDlg, setDelDlg] = useState(null);   // 删除确认弹窗（存待删除 dataIds）
  // 导入中标记：全屏 loading，从上传 excel 开始到完全导入成功结束
  const [importing, setImporting] = useState(false);
  // 导入进度文案：解析中 / 已导入 x/y（后端内存进度，前端轮询展示）
  const [importText, setImportText] = useState('正在导入数据，请稍候...');
  // 批量操作全屏进度：{ text } 非空时显示遮罩（已完成 11/500）
  const [batchInfo, setBatchInfo] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, size: pageSize };
      if (q.ownerName) params.ownerName = q.ownerName.trim();
      if (q.phone) params.phone = q.phone.trim();
      if (q.wechat) params.wechat = q.wechat.trim();
      if (q.community) params.community = q.community.trim();
      if (q.city) params.city = q.city.trim();
      if (q.workerName) params.workerName = q.workerName.trim();
      if (q.status && q.status !== 'ALL') params.status = q.status;
      const res = await parttimeApi.allocateSearch(params);
      setRows(res.rows || []);
      setTotal(res.total || 0);
    } catch (e) { MessagePlugin.error(e.message); } finally { setLoading(false); }
  }, [q, page, pageSize, reloadKey]);

  const loadTree = useCallback(async () => {
    try { setTree(await parttimeApi.workerTree()); } catch (e) { MessagePlugin.error(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTree(); }, [loadTree]);
  useEffect(() => { getPageSize(PAGE_SIZE_KEY, 500).then(setPageSize).catch(() => {}); }, []);
  // ESC 关闭图片全屏预览
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') setViewImg(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const selectedIds = () => [...sel];

  /**
   * 批量操作分批执行器：勾选量可能很大（500条），不能一次性提交（单事务过大易超时卡顿）。
   * 每批 BATCH_SIZE 条串行调用 fn(chunk)，实时更新全屏进度「已完成 x/y」。
   * 全部完成后返回，由调用方关闭遮罩并刷新列表。
   */
  const runBatches = async (ids, fn, textPrefix = '已完成') => {
    const BATCH_SIZE = 20;
    const total = ids.length;
    let done = 0;
    for (let i = 0; i < total; i += BATCH_SIZE) {
      const chunk = ids.slice(i, i + BATCH_SIZE);
      await fn(chunk);
      done += chunk.length;
      setBatchInfo({ text: `${textPrefix} ${Math.min(done, total)}/${total}` });
    }
  };

  // ---- 分页/翻页（支持每页条数切换，切换时回到第一页） ----
  const onPageChange = (info) => {
    const size = info && info.pageSize;
    const cur = typeof info === 'number' ? info : (info && info.current ? info.current : page);
    if (size && size !== pageSize) {
      setPageSize(size);
      setPage(1);
    } else if (cur) {
      setPage(cur);
    }
  };

  // ---- 导入excel（选中即导入，始终返回 Promise 供 Upload requestMethod 使用；轮询后端进度） ----
  const doImport = async (ctx) => {
    const raw = (ctx && ctx.raw) || (ctx && ctx.file && ctx.file.raw);
    if (!raw) return { status: 'fail', error: '未获取到文件' };
    // 全屏 loading：从上传开始一直显示到完全导入成功（大文件解析+入库耗时较长）
    setImporting(true);
    setImportText('正在解析 excel 数据...');
    // 轮询后端内存进度：解析阶段固定文案，入库阶段显示「已导入 x/y」
    const timer = setInterval(async () => {
      try {
        const p = await parttimeApi.importProgress();
        if (p && p.phase === 'saving') setImportText(`已导入 ${p.done}/${p.total}`);
      } catch (e) { /* 轮询失败忽略，不影响导入主流程 */ }
    }, 1000);
    try {
      const r = await parttimeApi.importConfirm(raw);
      MessagePlugin.success(`导入 ${r.count} 条`);
      setPage(1);
      load();
      return { status: 'success' };
    } catch (e) {
      MessagePlugin.error(e.message);
      return { status: 'fail', error: e.message };
    } finally {
      clearInterval(timer);
      setImporting(false);
      setImportText('正在导入数据，请稍候...');
    }
  };

  // 勾选状态由 TDesign Table 的 selectedRowKeys / onSelectChange 统一管理

  // ---- 打开编辑器 ----
  const openEditor = (mode, dataIds) => {
    setPlanState({});
    setScriptVal('');
    setQrcodeList([]);
    setRemarkVal('');
    // 单条编辑时预填该行当前值
    if (dataIds.length === 1) {
      const r = rows.find(x => x.dataId === dataIds[0]);
      if (r) {
        setScriptVal(r.script || '');
        setQrcodeList(r.qrcodeList || []);
        setRemarkVal(r.qrcodeRemark || '');
      }
    }
    setEditor({ mode, dataIds });
  };

  // ---- 保存：兼职树（逐条绑定，勾选量大时分批执行+进度） ----
  const saveWorkers = async () => {
    const plan = Object.entries(planState)
      .filter(([, v]) => v > 0)
      .map(([k, v]) => ({ userId: Number(k), count: Number(v) }));
    const sum = plan.reduce((s, p) => s + p.count, 0);
    if (!plan.length) { MessagePlugin.error('请勾选兼职并填写数量'); return; }
    if (sum > editor.dataIds.length) {
      MessagePlugin.error(`分配数量总和（${sum}）超过勾选数据（${editor.dataIds.length}）`);
      return;
    }
    // 把分配计划展开为逐条用户序列（保持与一次性分配一致的先后顺序），按批切分提交
    const seq = [];
    plan.forEach(p => { for (let i = 0; i < p.count; i++) seq.push(p.userId); });
    setBatchInfo({ text: `已完成 0/${editor.dataIds.length}` });
    try {
      let offset = 0;
      await runBatches(editor.dataIds, async (chunk) => {
        const sub = {};
        chunk.forEach(() => {
          const uid = seq[offset] != null ? seq[offset] : null;
          offset++;
          if (uid != null) sub[uid] = (sub[uid] || 0) + 1;
        });
        const subPlan = Object.entries(sub).map(([userId, count]) => ({ userId: Number(userId), count }));
        if (subPlan.length) await parttimeApi.assignWorkers(chunk, subPlan);
      });
      MessagePlugin.success('已指派兼职（未发送）');
      setEditor(null);
      load();
    } catch (e) { MessagePlugin.error(e.message); } finally { setBatchInfo(null); }
  };

  // ---- 保存：话术/备注（群二维码单独走 saveQrcodes；分批执行+进度） ----
  const saveField = async (field) => {
    const fields = {};
    if (field === 'script') {
      if (!scriptVal.trim()) { MessagePlugin.error('请输入话术'); return; }
      fields.script = scriptVal.trim();
    } else if (field === 'remark') {
      fields.qrcodeRemark = remarkVal.trim();
    }
    setBatchInfo({ text: `已完成 0/${editor.dataIds.length}` });
    try {
      await runBatches(editor.dataIds, (chunk) => parttimeApi.updateLayoutFields(chunk, fields));
      MessagePlugin.success('保存成功');
      setEditor(null);
      load();
    } catch (e) { MessagePlugin.error(e.message); } finally { setBatchInfo(null); }
  };

  // ---- 保存：群二维码（多张，替换该数据全部二维码；分批执行+进度） ----
  const saveQrcodes = async () => {
    setBatchInfo({ text: `已完成 0/${editor.dataIds.length}` });
    try {
      await runBatches(editor.dataIds, (chunk) => parttimeApi.saveQrcodes(chunk, qrcodeList));
      MessagePlugin.success('保存成功');
      setEditor(null);
      load();
    } catch (e) { MessagePlugin.error(e.message); } finally { setBatchInfo(null); }
  };

  // ---- 群二维码上传（上传 COS 后加入列表并立即保存，无需点保存；保存分批） ----
  const uploadQrcode = (ctx) => {
    const files = Array.isArray(ctx) ? ctx : [ctx];
    const raws = files.map(f => (f && f.raw) || (f && f.file && f.file.raw)).filter(Boolean);
    if (!raws.length) return Promise.resolve({ status: 'fail', error: '未获取到文件' });
    const newItems = [];
    let chain = Promise.resolve();
    raws.forEach((raw) => {
      chain = chain.then(() => parttimeApi.uploadToCos(raw).then(r => {
        newItems.push({ fileId: r.fileId, url: r.url });
      }));
    });
    return chain.then(() => {
      const next = [...qrcodeList, ...newItems];
      setQrcodeList(next);
      if (editor && editor.dataIds.length) {
        setBatchInfo({ text: `已完成 0/${editor.dataIds.length}` });
        runBatches(editor.dataIds, (chunk) => parttimeApi.saveQrcodes(chunk, next))
          .catch(e => MessagePlugin.error(e.message))
          .finally(() => setBatchInfo(null));
      }
      return { status: 'success' };
    }).catch(e => { MessagePlugin.error(e.message); return { status: 'fail', error: e.message }; });
  };

  // ---- 删除单张群二维码（本地移除并立即保存；保存分批） ----
  const removeQrcodeItem = (i) => {
    const next = qrcodeList.filter((_, j) => j !== i);
    setQrcodeList(next);
    if (editor && editor.dataIds.length) {
      setBatchInfo({ text: `已完成 0/${editor.dataIds.length}` });
      runBatches(editor.dataIds, (chunk) => parttimeApi.saveQrcodes(chunk, next))
        .catch(e => MessagePlugin.error(e.message))
        .finally(() => setBatchInfo(null));
    }
  };

  // ---- 编辑弹窗右下角【保存】：按模式分派 ----
  // 均分：把当前弹窗待分配的数据量均分到已勾选的兼职人员
  const doEqual = () => {
    const sel = Object.keys(planState).filter(k => planState[k] > 0);
    if (!sel.length) { MessagePlugin.error('请先勾选兼职人员'); return; }
    const total = editor ? editor.dataIds.length : 0;
    if (total <= 0) { MessagePlugin.error('没有可分配的数据量'); return; }
    const base = Math.floor(total / sel.length);
    const rem = total % sel.length;
    const next = { ...planState };
    sel.forEach((k, i) => { next[k] = base + (i < rem ? 1 : 0); });
    setPlanState(next);
    MessagePlugin.success('已均分');
  };

  const saveEditor = async () => {
    const m = editor.mode;
    setSaving(true);
    try {
      if (m === 'worker') return await saveWorkers();
      if (m === 'script') return await saveField('script');
      if (m === 'qrcode') return await saveQrcodes();
      if (m === 'remark') return await saveField('remark');
      return await saveFull();
    } finally { setSaving(false); }
  };

  // ---- full 模式：保存当前弹窗内所有已填字段（话术/二维码/备注；分批执行+进度） ----
  const saveFull = async () => {
    setBatchInfo({ text: `已完成 0/${editor.dataIds.length}` });
    try {
      const fields = {};
      if (scriptVal && scriptVal.trim()) fields.script = scriptVal.trim();
      if (remarkVal && remarkVal.trim()) fields.qrcodeRemark = remarkVal.trim();
      if (Object.keys(fields).length) {
        await runBatches(editor.dataIds, (chunk) => parttimeApi.updateLayoutFields(chunk, fields));
      }
      if (qrcodeList.length) {
        await runBatches(editor.dataIds, (chunk) => parttimeApi.saveQrcodes(chunk, qrcodeList));
      }
      MessagePlugin.success('保存成功');
      setEditor(null);
      load();
    } catch (e) { MessagePlugin.error(e.message); } finally { setBatchInfo(null); }
  };

  // ---- 发送给兼职人员（分批执行+进度） ----
  const sendAll = async () => {
    if (!sel.size) { MessagePlugin.error('请先勾选记录'); return; }
    const ids = selectedIds();
    setSending(true);
    setBatchInfo({ text: `已完成 0/${ids.length}` });
    try {
      await runBatches(ids, (chunk) => parttimeApi.sendAssigned(chunk));
      MessagePlugin.success('已发送给兼职人员');
      setSel(new Set());
      load();
    } catch (e) { MessagePlugin.error(e.message); } finally { setSending(false); setBatchInfo(null); }
  };

  // ---- 删除勾选数据（受控确认弹窗，点击遮罩/按钮均可关闭） ----
  const deleteSelected = () => {
    const ids = selectedIds();
    if (!ids.length) { MessagePlugin.error('请先勾选记录'); return; }
    setDelDlg(ids);
  };
  const confirmDelete = async () => {
    setDeleting(true);
    setBatchInfo({ text: `已完成 0/${delDlg.length}` });
    try {
      await runBatches(delDlg, (chunk) => parttimeApi.deleteDatas(chunk));
      MessagePlugin.success(`已删除 ${delDlg.length} 条数据`);
      setDelDlg(null);
      setSel(new Set());
      setPage(1);
      load();
    } catch (e) { MessagePlugin.error(e.message); } finally { setDeleting(false); setBatchInfo(null); }
  };

  // TDesign Table 列定义（无斑马纹/无边框/hover/宽自适应/表头/多选/内置分页）
  const columns = [
    { colKey: 'row-select', type: 'multiple', width: 48 },
    { colKey: 'ownerName', title: '业主姓名', ellipsis: true },
    { colKey: 'phone', title: '手机号', ellipsis: true },
    { colKey: 'wechat', title: '微信号', ellipsis: true },
    { colKey: 'city', title: '城市', ellipsis: true },
    { colKey: 'community', title: '小区', ellipsis: true },
    {
      colKey: 'assignUserName', title: '兼职人员',
      cell: ({ row }) => (row.assignUserName ? <Tag theme="primary" variant="light">{row.assignUserName}</Tag> : null),
    },
    {
      colKey: 'script', title: '话术', ellipsis: true,
      cell: ({ row }) => row.script || null,
    },
    {
      colKey: 'qrcodeUrl', title: '群二维码',
      cell: ({ row }) => (row.qrcodeList && row.qrcodeList.length)
        ? <span style={{ color: '#2fa84f' }}>×{row.qrcodeList.length}</span> : null,
    },
    {
      colKey: 'qrcodeRemark', title: '群二维码备注', ellipsis: true,
      cell: ({ row }) => row.qrcodeRemark || null,
    },
    {
      colKey: 'status', title: '处理状态',
      cell: ({ row }) => <Tag theme={statusTheme2(row.status)} variant="light">{STATUS_LABEL[row.status] || ''}</Tag>,
    },
    {
      colKey: 'rejectReason', title: '兼职拒绝原因', ellipsis: true,
      cell: ({ row }) => (row.rejectReason ? <span title={row.rejectReason} style={{ display: 'block', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.rejectReason}</span> : null),
    },
    {
      colKey: 'op', title: '操作', width: 150,
      cell: ({ row }) => (
        <Space>
          <Button variant="text" theme="primary" size="small" onClick={() => openEditor('full', [row.dataId])}>编辑</Button>
          <Button variant="text" theme="primary" size="small" onClick={() => setViewRow(row)}>查看</Button>
        </Space>
      ),
    },
  ];

  // 编辑器弹窗内容渲染（保存按钮统一放弹窗右下角）
  const renderEditor = () => {
    const m = editor.mode;
    // 数组：需要展示的分段
    const showAll = m === 'full';
    return (
      <div>
        {(m === 'worker') && (
          <div style={{ marginBottom: 18 }}>
            {/* 标题行：「兼职人员」标题 + 均分按钮（均分到已勾选兼职） */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
              <h4 className="pt-title" style={{ fontSize: 14, margin: 0 }}>兼职人员</h4>
              <Button size="small" theme="primary" variant="text" onClick={doEqual}>均分（共 {editor.dataIds.length} 条）</Button>
            </div>
            <WorkerTree
              tree={tree}
              value={planState}
              onChange={setPlanState}
            />
          </div>
        )}
        {(showAll || m === 'script') && (
          <div style={{ marginBottom: 18 }}>
            <h4 className="pt-title" style={{ fontSize: 14 }}>话术</h4>
            <Textarea className="pt-textarea-lg" style={{ width: '100%' }} placeholder="请输入话术" value={scriptVal} onChange={(v) => setScriptVal(v)} />
          </div>
        )}
        {(showAll || m === 'qrcode') && (
          <div style={{ marginBottom: 18 }}>
            <h4 className="pt-title" style={{ fontSize: 14 }}>群二维码</h4>
            <Upload
              theme="file"
              multiple
              accept="image/*"
              requestMethod={uploadQrcode}
              fileListDisplay={() => null}
            >
              <Button theme="primary" variant="text" size="small">上传群二维码</Button>
            </Upload>
            {qrcodeList.length > 0 && (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 10 }}>
                {qrcodeList.map((q, i) => (
                  <div key={i} style={{ position: 'relative', width: 80, height: 80 }}>
                    <img src={q.url} alt="qrcode" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, cursor: 'zoom-in' }} onClick={() => setViewImg(q.url)} />
                    <span
                      style={{ position: 'absolute', top: -8, right: -8, width: 20, height: 20, lineHeight: '18px', textAlign: 'center', borderRadius: '50%', background: '#d54941', color: '#fff', cursor: 'pointer', fontSize: 13 }}
                      onClick={() => removeQrcodeItem(i)}>×</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {(showAll || m === 'remark') && (
          <div style={{ marginBottom: 8 }}>
            <h4 className="pt-title" style={{ fontSize: 14 }}>群二维码备注</h4>
            <Textarea className="pt-textarea-lg" style={{ width: '100%' }} placeholder="请输入群二维码备注" value={remarkVal} onChange={(v) => setRemarkVal(v)} />
          </div>
        )}
      </div>
    );
  };

  const editorTitle = editor && (
    editor.mode === 'worker' ? '修改兼职人员'
      : editor.mode === 'script' ? '修改话术'
      : editor.mode === 'qrcode' ? '修改群二维码'
      : editor.mode === 'remark' ? '修改群二维码备注'
      : '编辑数据'
  );

  return (
    <div className="pt-app">
      {/* 导入中全屏 loading：从上传 excel 开始一直显示到完全导入成功，实时显示进度 */}
      <Loading loading={importing} fullscreen text={importText} />
      {/* 批量操作全屏进度（已完成 x/y） */}
      <Loading loading={!!batchInfo} fullscreen text={batchInfo ? batchInfo.text : ''} />
      {/* 整体页面容器：导入 + 搜索 + 按钮 + 列表 为一个整体 */}
      <div className="pt-card" style={{ padding: 20 }}>
      {/* 导入excel */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 14, borderBottom: '1px solid #f0f0f0' }}>
          <span className="pt-title" style={{ margin: 0, lineHeight: '32px' }}>导入excel</span>
          <Upload
            theme="file"
            accept=".xlsx,.xls"
            requestMethod={doImport}
            className="pt-upload-inline"
            fileListDisplay={({ files, onRemove }) => (
              files.map(file => (
                <span key={file.uid || file.name} className="pt-upload-file">
                  <span className="pt-upload-name" title={file.name}>{file.name}</span>
                  <Button size="small" theme="danger" variant="text" onClick={() => onRemove(file)}>移除</Button>
                </span>
              ))
            )}
          />
          <span style={{ color: '#999', fontSize: 12 }}>上传后按「记录ID」幂等更新，手机号/微信号至少其一有值才入库</span>
        </div>

      {/* 搜索区 */}
      <div style={{ padding: '14px 0', borderBottom: '1px solid #f0f0f0' }}>
        <p className="pt-search-title">数据分配</p>
        <div className="pt-search-row">
          <span className="pt-search-label">业主姓名</span>
          <Input placeholder="业主张三" value={filters.ownerName} onChange={(v) => setFilters(f => ({ ...f, ownerName: v }))} style={{ width: 160 }} clearable />
          <span className="pt-search-label">业主手机号</span>
          <Input placeholder="138****" value={filters.phone} onChange={(v) => setFilters(f => ({ ...f, phone: v }))} style={{ width: 160 }} clearable />
          <span className="pt-search-label">业主微信</span>
          <Input placeholder="微信号" value={filters.wechat} onChange={(v) => setFilters(f => ({ ...f, wechat: v }))} style={{ width: 160 }} clearable />
          <span className="pt-search-label">小区名称</span>
          <Input placeholder="小区关键词" value={filters.community} onChange={(v) => setFilters(f => ({ ...f, community: v }))} style={{ width: 160 }} clearable />
          <span className="pt-search-label">城市</span>
          <Input placeholder="城市" value={filters.city} onChange={(v) => setFilters(f => ({ ...f, city: v }))} style={{ width: 140 }} clearable />
          <span className="pt-search-label">兼职人员</span>
          <Input placeholder="姓名/登录账号" value={filters.workerName} onChange={(v) => setFilters(f => ({ ...f, workerName: v }))} style={{ width: 160 }} clearable />
          <span className="pt-search-label">处理状态</span>
          <Select value={filters.status} options={STATUS_OPTS} onChange={(v) => setFilters(f => ({ ...f, status: v }))} style={{ width: 140 }} />
          <Button theme="primary" variant="text" size="small" loading={loading} onClick={() => { setPage(1); setQ(filters); setReloadKey(k => k + 1); }}>查询</Button>
          <Button theme="primary" variant="text" size="small" onClick={() => {
            setFilters({ ownerName: '', phone: '', wechat: '', community: '', city: '', status: 'ALL', workerName: '' });
            setQ({ ownerName: '', phone: '', wechat: '', community: '', city: '', status: 'ALL', workerName: '' });
            setPage(1);
          }}>重置</Button>
        </div>
      </div>

      {/* 批量操作按钮区（未勾选时点击提示；按钮外观始终一致，不随勾选变化） */}
      <div style={{ padding: '14px 0', borderBottom: '1px solid #f0f0f0' }}>
        <Space size={12} wrap>
          <Tooltip content="修改勾选数据的话术">
            <Button theme="primary" variant="text" size="small" onClick={() => { if (!sel.size) { MessagePlugin.warning('请先勾选数据'); return; } openEditor('script', selectedIds()); }}>修改话术</Button>
          </Tooltip>
          <Tooltip content="修改勾选数据的群二维码">
            <Button theme="primary" variant="text" size="small" onClick={() => { if (!sel.size) { MessagePlugin.warning('请先勾选数据'); return; } openEditor('qrcode', selectedIds()); }}>修改群二维码</Button>
          </Tooltip>
          <Tooltip content="修改勾选数据的群二维码备注">
            <Button theme="primary" variant="text" size="small" onClick={() => { if (!sel.size) { MessagePlugin.warning('请先勾选数据'); return; } openEditor('remark', selectedIds()); }}>修改群二维码备注</Button>
          </Tooltip>
          <Tooltip content="用兼职树给勾选数据指派兼职人员">
            <Button theme="primary" variant="text" size="small" onClick={() => { if (!sel.size) { MessagePlugin.warning('请先勾选数据'); return; } openEditor('worker', selectedIds()); }}>修改兼职人员</Button>
          </Tooltip>
          <Tooltip content="将勾选数据发送给兼职人员（小程序可见）">
            <Button theme="primary" variant="text" size="small" loading={sending} disabled={sending} onClick={() => { if (!sel.size) { MessagePlugin.warning('请先勾选数据'); return; } sendAll(); }}>发送给兼职人员</Button>
          </Tooltip>
          <Tooltip content="物理删除勾选数据（不可恢复）">
            <Button theme="danger" variant="text" size="small" onClick={() => { if (!sel.size) { MessagePlugin.warning('请先勾选数据'); return; } deleteSelected(); }}>删除</Button>
          </Tooltip>
          <span style={{ color: '#888' }}>已勾选 {sel.size} 条</span>
        </Space>
      </div>

      {/* 列表 */}
      <div style={{ paddingTop: 14 }}>
        <Table
          rowKey="dataId"
          resizable
          data={rows}
          loading={loading}
          hover
          size="small"
          columns={columns}
          selectedRowKeys={[...sel]}
          onSelectChange={(keys) => setSel(new Set(keys))}
          empty={<span>暂无数据</span>}
          pagination={{
            current: page, pageSize, total,
            // 下拉可选每页条数：100/300/500（数据库配置默认值已改为 500）
            pageSizeOptions: [100, 300, 500],
            onChange: onPageChange,
            totalContent: (t) => `共 ${t} 条数据`,
          }}
        />
      </div>
      </div> {/* 整体页面容器结束 */}

      {/* 编辑数据弹窗（保存/取消放右下角 link 按钮，点击遮罩可关闭） */}
      <Dialog header={editorTitle} visible={!!editor} width="560px" closeOnOverlayClick onClose={() => setEditor(null)}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button theme="default" variant="text" size="small" onClick={() => setEditor(null)}>取消</Button>
              <Button theme="primary" variant="text" size="small" loading={saving} disabled={saving} onClick={saveEditor}>保存</Button>
            </Space>
          </div>
        }>
        {editor && renderEditor()}
      </Dialog>

      {/* 查看详情 */}
      <Dialog header="数据详情" visible={!!viewRow} footer={null} width="560px" closeOnOverlayClick onClose={() => setViewRow(null)}>
        {viewRow && (
          <div>
            <Descriptions
              column={1}
              items={[
                { label: '业主姓名', content: viewRow.ownerName || '' },
                { label: '手机号', content: viewRow.phone || '' },
                { label: '微信号', content: viewRow.wechat || '' },
                { label: '城市', content: viewRow.city || '' },
                { label: '小区', content: viewRow.community || '' },
                { label: '兼职人员', content: viewRow.assignUserName || '' },
                { label: '话术', content: viewRow.script || '' },
              ]}
            />
            <div style={{ marginTop: 12 }}>
              <span style={{ color: '#666' }}>群二维码：</span>
              {(viewRow.qrcodeList && viewRow.qrcodeList.length)
                ? <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                    {viewRow.qrcodeList.map((q, i) => (
                      <img key={i} src={q.url} alt="qrcode" style={{ height: 72, borderRadius: 8, objectFit: 'cover', cursor: 'zoom-in' }} onClick={() => setViewImg(q.url)} />
                    ))}
                  </div>
                : ''}
            </div>
            <Descriptions
              column={1}
              items={[
                { label: '群二维码备注', content: viewRow.qrcodeRemark || '' },
                { label: '处理状态', content: <Tag theme={statusTheme2(viewRow.status)} variant="light">{STATUS_LABEL[viewRow.status] || viewRow.status || ''}</Tag> },
                { label: '对账状态', content: <Tag theme={settleTheme2(viewRow.settleStatus)} variant="light">{SETTLE_LABEL2[viewRow.settleStatus] || viewRow.settleStatus || ''}</Tag> },
                { label: '兼职拒绝原因', content: viewRow.rejectReason || '' },
              ]}
            />
          </div>
        )}
        <div style={{ marginTop: 16, textAlign: 'right' }}>
          <Button theme="default" variant="text" size="small" onClick={() => setViewRow(null)}>关闭</Button>
        </div>
      </Dialog>

      {/* 删除确认弹窗（受控，点击遮罩/按钮均可关闭） */}
      <Dialog header="删除确认" visible={!!delDlg} width="380px" closeOnOverlayClick onClose={() => setDelDlg(null)}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Space>
              <Button theme="default" variant="text" size="small" onClick={() => setDelDlg(null)}>取消</Button>
              <Button theme="danger" variant="text" size="small" loading={deleting} disabled={deleting} onClick={confirmDelete}>删除</Button>
            </Space>
          </div>
        }>
        <p style={{ margin: 0 }}>确定删除选中的 {delDlg ? delDlg.length : 0} 条客户数据吗？删除后数据库中不可恢复。</p>
      </Dialog>

      {/* 图片全屏预览（自定义：遮罩 + 图片，点击空白 / ESC 关闭） */}
      {viewImg && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', zIndex: 3000, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out' }}
          onClick={() => setViewImg(null)}
        >
          <img src={viewImg} alt="预览" style={{ maxWidth: '90%', maxHeight: '90%', objectFit: 'contain', borderRadius: 6 }} />
        </div>
      )}
    </div>
  );
}